import {AnyObject} from 'final-form'
import localforage from 'localforage'
import {MutableRefObject, useEffect} from 'react'
import {useForm} from 'react-final-form'
import {ErrorCode} from '../../../config/ErrorCode'
import {runAsync} from '../../../helpers/util'
import {useUnmountedRef} from '../../hocs/hooks'

const prefix = 'form-'
const AutoFormSave = <T extends AnyObject, V extends T = T>(
  {
    name,
    version = 'default',
    beforeSave,
    afterRestore,
    clearRef,
  }: {
    name: string
    version?: string
    beforeSave?: (val: T) => V | Promise<V>
    afterRestore?: (val: V) => any
    clearRef?: MutableRefObject<boolean>
  }) => {
  const form = useForm<T>()
  const unmountedRef = useUnmountedRef()
  const storageKey = `${prefix}${name}`
  useEffect(() => {
    runAsync(ErrorCode.getStoredData, (async () => {
      const storedData: {version: string, values: V} = await localforage.getItem(storageKey)
      if (storedData) {
        if (storedData.version && storedData.version !== version) await localforage.removeItem(storageKey)
        else if (!unmountedRef.current) {
          form.reset(storedData.values)
          afterRestore?.(storedData.values)
        }
      }
    })())
    return () => {
      const values = form.getState().values
      const clear = clearRef?.current
      runAsync(ErrorCode.autoFormSave, (async () => {
        if (clear) await localforage.removeItem(storageKey)
        else await localforage.setItem(storageKey, {
          version,
          values: beforeSave ? await beforeSave(values) : values
        })
      })())
    }
  }, [unmountedRef, storageKey, version, form, beforeSave, afterRestore, clearRef])
  return null
}
AutoFormSave.displayName = 'AutoFormSave'
export default AutoFormSave
